/**
 * FOUC 방지: 페이지 렌더링 전에 테마 설정 즉시 적용
 * ThemeConfig를 활용하여 CSS 변수 및 클래스를 동적으로 적용
 */

import { ThemeConfig } from './themeConfig';

// ThemeApply 객체
export const ThemeApply = {
  /**
   * 기본 설정값 가져오기 (ThemeConfig.defaultConfig 참조)
   * @returns {object} 기본 설정값
   */
  getDefaultConfig: function() {
    return ThemeConfig.defaultConfig || {};
  },

  // localStorage 키 (ThemeConfig와 동일하게 유지)
  STORAGE_KEY: 'themeConfig_local',

  // 네비게이션 오프셋 리스너 등록 여부 플래그
  navOffsetsListenersAttached: false,

  // 테마 영역 표시 여부 플래그
  themeAreasShown: false,

  /**
   * 테마 정보에 따라 각 영역 동적 표시
   */
  showThemeAreas: function() {
    if (this.themeAreasShown) return;
    this.themeAreasShown = true;

    const navLayout = ThemeConfig ? ThemeConfig.get('navLayout') : 'top';
    const defaultConfig = this.getDefaultConfig();
    const layout = navLayout || defaultConfig.navLayout || 'top';

    // 헤더 영역 표시
    const headerContainer = document.getElementById('theme-loading-container');
    if (headerContainer) {
      while (headerContainer.firstChild) {
        const child = headerContainer.firstChild;
        if (child.tagName === 'SCRIPT') {
          document.head.appendChild(child);
        } else {
          document.body.insertBefore(child, document.body.firstChild);
        }
      }
      headerContainer.remove();
    }

    // 네비게이션 레이아웃에 따라 좌측/우측 네비게이션 표시
    const navLeftContainer = document.getElementById('theme-loading-nav-left');
    const navRightContainer = document.getElementById('theme-loading-nav-right');

    // 좌측 네비게이션 처리
    if (navLeftContainer) {
      const navLeftElement = navLeftContainer.querySelector('.nav-layout') ||
                             Array.from(navLeftContainer.children).find(child => child.nodeType === 1);

      if (navLeftElement) {
        document.body.appendChild(navLeftElement);
        if (layout === 'left') {
          navLeftElement.style.display = '';
        } else {
          navLeftElement.style.display = 'none';
        }
      } else {
        while (navLeftContainer.firstChild) {
          const child = navLeftContainer.firstChild;
          if (child.nodeType === 1 && child.style) {
            document.body.appendChild(child);
            if (layout === 'left') {
              child.style.display = '';
            } else {
              child.style.display = 'none';
            }
          } else {
            navLeftContainer.removeChild(child);
          }
        }
      }
      navLeftContainer.remove();
    }

    // 우측 네비게이션 처리
    if (navRightContainer) {
      const navRightElement = navRightContainer.querySelector('.nav-layout') ||
                              Array.from(navRightContainer.children).find(child => child.nodeType === 1);

      if (navRightElement) {
        document.body.appendChild(navRightElement);
        if (layout === 'right') {
          navRightElement.style.display = '';
        } else {
          navRightElement.style.display = 'none';
        }
      } else {
        while (navRightContainer.firstChild) {
          const child = navRightContainer.firstChild;
          if (child.nodeType === 1 && child.style) {
            document.body.appendChild(child);
            if (layout === 'right') {
              child.style.display = '';
            } else {
              child.style.display = 'none';
            }
          } else {
            navRightContainer.removeChild(child);
          }
        }
      }
      navRightContainer.remove();
    }

    // 메인 콘텐츠 영역 표시
    const mainWrapper = document.getElementById('theme-loading-main-wrapper');
    if (mainWrapper) {
      mainWrapper.style.display = '';
      mainWrapper.removeAttribute('id');
    }

    // 푸터 영역 표시
    const footerContainer = document.getElementById('theme-loading-footer');
    if (footerContainer) {
      while (footerContainer.firstChild) {
        document.body.appendChild(footerContainer.firstChild);
      }
      footerContainer.remove();
    }

    // 사이드바 메뉴 표시
    const sidebarMenuContainer = document.getElementById('theme-loading-sidebar-menu');
    if (sidebarMenuContainer) {
      while (sidebarMenuContainer.firstChild) {
        document.body.appendChild(sidebarMenuContainer.firstChild);
      }
      sidebarMenuContainer.remove();
    }

    // 사이드바 공통 표시
    const sidebarCommonContainer = document.getElementById('theme-loading-sidebar-common');
    if (sidebarCommonContainer) {
      while (sidebarCommonContainer.firstChild) {
        document.body.appendChild(sidebarCommonContainer.firstChild);
      }
      sidebarCommonContainer.remove();
    }
  },

  /**
   * 깊은 병합 함수
   */
  deepMerge: function(target, ...sources) {
    if (!sources.length) return target;
    const source = sources.shift();

    if (this.isObject(target) && this.isObject(source)) {
      for (const key in source) {
        if (this.isObject(source[key])) {
          if (!target[key]) Object.assign(target, { [key]: {} });
          this.deepMerge(target[key], source[key]);
        } else {
          Object.assign(target, { [key]: source[key] });
        }
      }
    }

    return this.deepMerge(target, ...sources);
  },

  /**
   * 객체 여부 확인
   */
  isObject: function(item) {
    return item && typeof item === 'object' && !Array.isArray(item);
  },

  /**
   * ThemeConfig 로드 및 CSS 변수 즉시 적용
   */
  loadAndApplyTheme: function() {
    try {
      // ThemeConfig.initialize()가 이미 실행되어 window.__themeConfig에 최신 설정이 들어있다고 가정
      const config = (typeof window !== 'undefined' && window.__themeConfig) || this.getDefaultConfig();
      const root = document.documentElement;

      // 1. 테마 색상 (주/보조)
      if (config.themeColor) {
        root.style.setProperty('--theme-color-primary', config.themeColor);
      }
      const themeSecondaryDefault = '#146e7a';
      const secondaryResolved =
        config.themeColorSecondary != null && String(config.themeColorSecondary).trim() !== ''
          ? String(config.themeColorSecondary).trim()
          : themeSecondaryDefault;
      root.style.setProperty('--theme-color-secondary', secondaryResolved);

      // 1-1. 최상단 영역 배경색
      if (config.headerTopBackgroundColor) {
        root.style.setProperty('--header-top-bg-color', config.headerTopBackgroundColor);
      }

      if (config.headerTopTextColor) {
        root.style.setProperty('--header-top-text-color', config.headerTopTextColor);
      }

      // 1-2. 메뉴 영역 배경색 및 글자색
      if (config.headerBottomBackgroundColor) {
        root.style.setProperty('--header-bottom-bg-color', config.headerBottomBackgroundColor);
        const headerBottomEl = document.querySelector('.header-bottom');
        if (headerBottomEl) {
          headerBottomEl.style.backgroundColor = config.headerBottomBackgroundColor;
        }
        const navLayoutEls = document.querySelectorAll('.nav-layout');
        navLayoutEls.forEach(function(el) {
          el.style.backgroundColor = config.headerBottomBackgroundColor;
        });
      }

      if (config.headerBottomTextColor) {
        root.style.setProperty('--header-bottom-text-color', config.headerBottomTextColor);
        const headerBottomEl = document.querySelector('.header-bottom');
        if (headerBottomEl) {
          headerBottomEl.style.color = config.headerBottomTextColor;
        }
        const menuLinks = document.querySelectorAll('.header-bottom .main-menu-item > a');
        menuLinks.forEach(function(link) {
          link.style.color = config.headerBottomTextColor;
        });
        const submenuLinks = document.querySelectorAll('.header-bottom .submenu li a');
        submenuLinks.forEach(function(link) {
          link.style.color = config.headerBottomTextColor;
        });
        const navLayoutEls = document.querySelectorAll('.nav-layout');
        navLayoutEls.forEach(function(el) {
          el.style.color = config.headerBottomTextColor;
        });
        const navTreeLinks = document.querySelectorAll('.nav-layout .nav-tree__link');
        navTreeLinks.forEach(function(link) {
          link.style.color = config.headerBottomTextColor;
        });
        const navInputs = document.querySelectorAll('.nav-layout .nav-search-wrap form input');
        navInputs.forEach(function(input) {
          input.style.color = config.headerBottomTextColor;
        });
      }

      // 1-3. 메뉴 영역 높이
      if (config.headerBottomHeight !== undefined && config.headerBottomHeight !== null) {
        const heightPx = config.headerBottomHeight + 'px';
        root.style.setProperty('--header-bottom-padding', heightPx);
        root.style.setProperty('--header-bottom-padding-bottom', heightPx);
        const headerBottomEl = document.querySelector('.header-bottom');
        if (headerBottomEl) {
          headerBottomEl.style.paddingTop = heightPx;
          headerBottomEl.style.paddingBottom = heightPx;
        }
        const navLayoutEls = document.querySelectorAll('.nav-layout');
        navLayoutEls.forEach(function(el) {
          el.style.paddingTop = heightPx;
          el.style.paddingBottom = heightPx;
        });
      }

      // 1-4. 최상단 영역 높이
      if (config.headerTopHeight !== undefined && config.headerTopHeight !== null) {
        const heightPx = config.headerTopHeight + 'px';
        root.style.setProperty('--header-top-padding', heightPx);
        const headerTopEl = document.querySelector('.header-top');
        if (headerTopEl) {
          headerTopEl.style.paddingTop = heightPx;
          headerTopEl.style.paddingBottom = heightPx;
        }
      }

      // 1-5. 최상단 영역 표시/숨김
      if (config.headerTopVisible !== undefined && config.headerTopVisible !== null) {
        const headerTopEl = document.querySelector('.header-top');
        if (headerTopEl) {
          if (config.headerTopVisible === true || config.headerTopVisible === 'true') {
            headerTopEl.classList.remove('hidden');
            headerTopEl.style.removeProperty('display');
          } else {
            headerTopEl.classList.add('hidden');
            headerTopEl.style.removeProperty('display');
          }
        }
        this.updateNavOffsets();
      }

      // 2. 그리드 간격
      if (config.gridGap !== undefined && config.gridGap !== null) {
        root.style.setProperty('--grid-gap', config.gridGap.toFixed(2) + 'rem');
      }

      // 3. 컨테이너 너비
      if (config.containerWidths) {
        Object.keys(config.containerWidths).forEach(function(size) {
          if (config.containerWidths[size] !== undefined && config.containerWidths[size] !== null) {
            root.style.setProperty('--container-max-width-' + size, config.containerWidths[size] + 'px');
          }
        });
      }

      // 4. 영역별 컨테이너 너비
      if (config.areaContainerWidths) {
        Object.keys(config.areaContainerWidths).forEach(function(area) {
          const areaWidths = config.areaContainerWidths[area];
          if (areaWidths) {
            Object.keys(areaWidths).forEach(function(size) {
              if (areaWidths[size] !== undefined && areaWidths[size] !== null) {
                const varName = '--container-max-width-' + area + '-' + size;
                root.style.setProperty(varName, areaWidths[size] + 'px');
              }
            });
          }
        });
      }

      // 4-1. 동적 미디어 쿼리 생성 (브레이크포인트 기반)
      this.generateDynamicMediaQueries(config);

      // 5. 폰트 스택
      if (config.fontStack) {
        root.style.setProperty('--bs-font-sans-serif', config.fontStack);
      }

      // 6. 메가메뉴 컬럼 수
      if (config.megaMenuColumns !== undefined && config.megaMenuColumns !== null) {
        const columns = parseInt(config.megaMenuColumns, 10);
        if (!isNaN(columns) && columns >= 1 && columns <= 3) {
          root.style.setProperty('--mega-menu-columns', columns.toString());
        }
      }

      // 7. 우측 사이드바 너비
      if (config.rightSidebarWidth !== undefined && config.rightSidebarWidth !== null) {
        const widthPx = config.rightSidebarWidth + 'px';
        root.style.setProperty('--right-sidebar-width', widthPx);
        const sidebarElements = document.querySelectorAll('.sidebar-common-active');
        sidebarElements.forEach(function(el) {
          el.style.width = widthPx;
        });
      }

      // 설정을 전역 변수에 저장 (이미 되어있겠지만 재확인)
      if (typeof window !== 'undefined') {
        window.__themeConfig = config;
      }
    } catch (e) {
      console.warn('테마 적용 중 오류:', e);
      if (typeof window !== 'undefined') {
        window.__themeConfig = this.getDefaultConfig();
      }
    }
  },

  /**
   * 동적 미디어 쿼리 생성 (브레이크포인트 기반)
   * 테마 설정의 containerWidths를 기반으로 CSS 미디어 쿼리를 동적으로 생성
   * @param {object} config - 테마 설정 객체
   */
  generateDynamicMediaQueries: function(config) {
    if (typeof document === 'undefined') return;

    const containerWidths = config.containerWidths || {};
    const areaContainerWidths = config.areaContainerWidths || {};

    // 브레이크포인트 순서 (큰 것부터)
    const breakpoints = ['xl', 'lg', 'md', 'sm'];

    // 영역별 셀렉터 매핑
    const areaSelectors = {
      'header-top': ['.header-top .container'],
      'header-bottom': ['.header-bottom .container'],
      'banner': ['#banner-container .container'],
      'content': ['.main-wrapper .container'],
      'footer': ['.footer-area .container'],
      'related-sites': ['.related-sites-section .container', '.related-sites-section.container']
    };

    let css = '/* 동적 미디어 쿼리 - 테마 설정 기반 */\n';

    // 1. XL 이상 (min-width만)
    const xlWidth = containerWidths.xl;
    if (xlWidth && xlWidth > 0) {
      css += `@media only screen and (min-width: ${xlWidth}px) {\n`;
      css += `  .container {\n`;
      css += `    max-width: var(--container-max-width-xl, ${xlWidth}px);\n`;
      css += `  }\n`;

      // 영역별 컨테이너 너비
      Object.keys(areaContainerWidths).forEach((area) => {
        const areaWidths = areaContainerWidths[area];
        const selectors = areaSelectors[area] || [`.${area}-section .container`, `.${area}-section.container`];

        selectors.forEach((selector) => {
          css += `  ${selector} {\n`;
          if (areaWidths && areaWidths.xl !== undefined && areaWidths.xl !== null) {
            css += `    max-width: var(--container-max-width-${area}-xl, var(--container-max-width-xl, ${xlWidth}px));\n`;
          } else {
            css += `    max-width: var(--container-max-width-${area}-xl, var(--container-max-width-xl, ${xlWidth}px));\n`;
          }
          css += `  }\n`;
        });
      });

      css += `}\n\n`;
    }

    // 2. 각 브레이크포인트 범위 (min-width와 max-width 조합)
    for (let i = 0; i < breakpoints.length - 1; i++) {
      const currentSize = breakpoints[i];
      const nextSize = breakpoints[i + 1];

      const currentWidth = containerWidths[currentSize];
      const nextWidth = containerWidths[nextSize];

      if (!currentWidth || currentWidth <= 0 || !nextWidth || nextWidth <= 0) continue;

      const minWidth = nextWidth;
      const maxWidth = currentWidth - 1;

      css += `/* ${currentSize.toUpperCase()} (${minWidth}px ~ ${maxWidth}px) */\n`;
      css += `@media only screen and (min-width: ${minWidth}px) and (max-width: ${maxWidth}px) {\n`;
      css += `  .container {\n`;
      css += `    max-width: var(--container-max-width-${currentSize}, ${currentWidth}px);\n`;
      css += `  }\n`;

      // 영역별 컨테이너 너비
      // eslint-disable-next-line no-loop-func
      Object.keys(areaContainerWidths).forEach((area) => {
        const areaWidths = areaContainerWidths[area];
        const selectors = areaSelectors[area] || [`.${area}-section .container`, `.${area}-section.container`];

        // eslint-disable-next-line no-loop-func
        selectors.forEach((selector) => {
          css += `  ${selector} {\n`;
          if (areaWidths && areaWidths[currentSize] !== undefined && areaWidths[currentSize] !== null) {
            css += `    max-width: var(--container-max-width-${area}-${currentSize}, var(--container-max-width-${currentSize}, ${currentWidth}px));\n`;
          } else {
            css += `    max-width: var(--container-max-width-${area}-${currentSize}, var(--container-max-width-${currentSize}, ${currentWidth}px));\n`;
          }
          css += `  }\n`;
        });
      });

      css += `}\n\n`;
    }

    // 3. SM 이하 (max-width만) - md의 max-width 사용
    const mdWidth = containerWidths.md;
    if (mdWidth && mdWidth > 0) {
      const maxWidth = mdWidth - 1;

      css += `/* SM 이하 (max-width: ${maxWidth}px) */\n`;
      css += `@media only screen and (max-width: ${maxWidth}px) {\n`;
      css += `  .container {\n`;
      css += `    max-width: var(--container-max-width-sm, ${containerWidths.sm || 576}px);\n`;
      css += `  }\n`;

      // 영역별 컨테이너 너비
      Object.keys(areaContainerWidths).forEach((area) => {
        const areaWidths = areaContainerWidths[area];
        const selectors = areaSelectors[area] || [`.${area}-section .container`, `.${area}-section.container`];

        selectors.forEach((selector) => {
          css += `  ${selector} {\n`;
          if (areaWidths && areaWidths.sm !== undefined && areaWidths.sm !== null) {
            css += `    max-width: var(--container-max-width-${area}-sm, var(--container-max-width-sm, ${containerWidths.sm || 576}px));\n`;
          } else {
            css += `    max-width: var(--container-max-width-${area}-sm, var(--container-max-width-sm, ${containerWidths.sm || 576}px));\n`;
          }
          css += `  }\n`;
        });
      });

      css += `}\n\n`;
    }

    // 4. 추가: max-width만 사용하는 간단한 패턴 (기존 호환성)
    breakpoints.forEach((size) => {
      const width = containerWidths[size];
      if (!width || width <= 0) return;

      const maxWidth = width - 1;

      Object.keys(areaContainerWidths).forEach((area) => {
        const areaWidths = areaContainerWidths[area];
        const selectors = areaSelectors[area] || [`.${area}-section .container`, `.${area}-section.container`];

        selectors.forEach((selector) => {
          css += `@media (max-width: ${maxWidth}px) {\n`;
          css += `  ${selector} {\n`;

          if (areaWidths && areaWidths[size] !== undefined && areaWidths[size] !== null) {
            css += `    max-width: var(--container-max-width-${area}-${size}, var(--container-max-width-${size}, ${width}px));\n`;
          } else {
            css += `    max-width: var(--container-max-width-${size}, ${width}px);\n`;
          }

          css += `  }\n`;
          css += `}\n\n`;
        });
      });
    });

    // 동적 스타일 태그 생성/업데이트
    let styleTag = document.getElementById('theme-dynamic-media-queries');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'theme-dynamic-media-queries';
      styleTag.setAttribute('data-theme-dynamic', 'true');
      document.head.appendChild(styleTag);
    }
    styleTag.textContent = css;
  },

  /**
   * 네비게이션 오프셋 업데이트 함수
   * CSS 변수 대신 window 전역변수를 사용하여 동적 값 관리
   */
  updateNavOffsets: function() {
    let topOffset = 0;
    let bottomOffset = 0;

    try {
      const headerTopEl = document.querySelector('.header-top');
      if (headerTopEl) {
        const rect = headerTopEl.getBoundingClientRect();
        const computedStyle = window.getComputedStyle(headerTopEl);

        const isHidden = headerTopEl.classList.contains('hidden') ||
                        computedStyle.display === 'none' ||
                        computedStyle.visibility === 'hidden';

        if (isHidden) {
          topOffset = 0;
        } else {
          topOffset = Math.max(0, rect.bottom);
        }
      }
    } catch (e) {
      topOffset = 0;
    }

    let footerHeight = 0;
    try {
      const footerEl = document.querySelector('.footer-area');
      if (footerEl) {
        footerHeight = footerEl.offsetHeight || footerEl.getBoundingClientRect().height || 0;

        const rect = footerEl.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          bottomOffset = Math.max(0, window.innerHeight - rect.top);
        } else {
          bottomOffset = 0;
        }
      }
    } catch (e) {
      bottomOffset = 0;
      footerHeight = 0;
    }

    // window 전역변수에 동적 값 설정 (CSS 변수는 테마 정보용으로만 사용)
    const headerTopOffset = Math.max(0, Math.round(topOffset));
    const footerOffsetValue = Math.max(0, Math.round(bottomOffset));
    const footerHeightValue = Math.max(0, Math.round(footerHeight));
    const navTopOffset = Math.max(0, Math.round(topOffset));
    const navBottomOffset = Math.max(0, Math.round(bottomOffset));

    window.headerTopOffset = headerTopOffset;
    window.footerOffset = footerOffsetValue;
    window.footerHeight = footerHeightValue;
    window.navTopOffset = navTopOffset;
    window.navBottomOffset = navBottomOffset;
    window.sidebarWrapperTop = Math.max(0, Math.round(topOffset + 10));

    // sidebar-scroll-offset.js의 applySidebarTop 메서드 호출하여 DOM에 직접 적용
    if (window.SidebarScrollOffset && typeof window.SidebarScrollOffset.applySidebarTop === 'function') {
      window.SidebarScrollOffset.applySidebarTop();
    }

    // nav-layout과 main-wrapper에 직접 적용
    this.applyNavOffsets();
  },

  /**
   * window 전역변수 값을 사용하여 nav-layout과 main-wrapper의 스타일 직접 설정
   */
  applyNavOffsets: function() {
    const navTopOffset = window.navTopOffset || window.headerTopOffset || 0;
    const navBottomOffset = window.navBottomOffset || window.footerOffset || 0;
    const headerTopOffset = window.headerTopOffset || 0;

    // .nav-layout 요소들에 적용
    const navLayouts = document.querySelectorAll('.nav-layout');
    navLayouts.forEach(navLayout => {
      navLayout.style.top = navTopOffset + 'px';
      navLayout.style.bottom = navBottomOffset + 'px';
    });

    // .main-wrapper 요소들에 적용
    const mainWrappers = document.querySelectorAll('.main-wrapper');
    mainWrappers.forEach(mainWrapper => {
      const isLeftOrRightLayout = document.body.classList.contains('nav-layout-left') ||
                                   document.body.classList.contains('nav-layout-right');
      const isTopLayout = document.body.classList.contains('nav-layout-top');

      if (isLeftOrRightLayout) {
        // 좌측/우측 배치: 헤더 높이만큼 margin-top 적용
        mainWrapper.style.marginTop = headerTopOffset + 'px';
        // min-height 계산: 100vh - nav-bottom-offset (또는 footer-offset)
        mainWrapper.style.minHeight = `calc(100vh - ${navBottomOffset}px)`;
      } else if (isTopLayout) {
        // 상단 배치: margin-top 제거 (헤더 바로 아래 배치)
        mainWrapper.style.marginTop = '';
        mainWrapper.style.minHeight = '';
      }
    });
  },

  /**
   * 스크롤 및 리사이즈 이벤트 리스너 등록
   */
  attachNavOffsetsListeners: function() {
    if (this.navOffsetsListenersAttached) return;
    this.navOffsetsListenersAttached = true;

    // window 전역변수 초기화
    if (typeof window.headerTopOffset === 'undefined') {
      window.headerTopOffset = 0;
    }
    if (typeof window.footerOffset === 'undefined') {
      window.footerOffset = 0;
    }
    if (typeof window.footerHeight === 'undefined') {
      window.footerHeight = 0;
    }
    if (typeof window.navTopOffset === 'undefined') {
      window.navTopOffset = 0;
    }
    if (typeof window.navBottomOffset === 'undefined') {
      window.navBottomOffset = 0;
    }
    if (typeof window.sidebarWrapperTop === 'undefined') {
      window.sidebarWrapperTop = 0;
    }

    const self = this;
    let rafId = null;
    function debouncedUpdateNavOffsets() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(function() {
        self.updateNavOffsets();
        rafId = null;
      });
    }

    window.addEventListener('scroll', debouncedUpdateNavOffsets, { passive: true });
    window.addEventListener('resize', debouncedUpdateNavOffsets, { passive: true });
    window.addEventListener('load', function() {
      requestAnimationFrame(function() {
        self.updateNavOffsets();
      });
    });

    // 초기 실행
    this.updateNavOffsets();
  },

  applyMenuScrollType: function(type) {
    const targetType = typeof type === 'string' ? type : 'wrap';
    const validType = ['wrap', 'scroll'].indexOf(targetType) >= 0 ? targetType : 'wrap';

    if (window.MenuScroll && typeof window.MenuScroll.applyMenuScrollType === 'function') {
      window.MenuScroll.applyMenuScrollType(validType);
    } else {
      const mainMenu = document.querySelector('.header-area .main-menu');
      if (mainMenu) {
        if (validType === 'scroll') {
          mainMenu.classList.add('main-menu-scroll');
        } else {
          mainMenu.classList.remove('main-menu-scroll');
        }
      }
    }
  },

  applyMenuAlignment: function(align) {
    const targetAlign = typeof align === 'string' ? align : 'left';
    const validAlign = ['left', 'right'].indexOf(targetAlign) >= 0 ? targetAlign : 'left';

    const menuLists = document.querySelectorAll('.header-area .main-menu > nav > ul.main-menu-list');
    menuLists.forEach(function(list) {
      list.style.justifyContent = 'flex-start';
    });

    const navs = document.querySelectorAll('.header-area .main-menu > nav');
    navs.forEach(function(nav) {
      const maxScrollLeft = Math.max(0, nav.scrollWidth - nav.clientWidth);
      nav.scrollLeft = validAlign === 'right' ? maxScrollLeft : 0;
    });
  },

  getBannerData: function() {
    if (window.BannerData) {
      return window.BannerData;
    }
    return {
      title: document.title || '페이지',
      breadcrumbPath: null,
      leftImg: '/assets/images/page/left_slide.png',
      rightImg: '/assets/images/page/right_slide.png',
      subtitle: '',
      description: '',
      icon: '📋',
      bgImage: '/assets/images/page/banner_bg.jpg',
      overlay: 'dark',
      bgGradient: ''
    };
  },

  toPascalCase: function(value) {
    if (typeof value !== 'string' || value.length === 0) {
      return '';
    }
    return value
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map(function(part) {
        return part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join('');
  },

  renderBannerContent: function(bannerType) {
    const container = document.getElementById('banner-container');
    if (!container || !window.BannerRenderer) {
      return false;
    }

    const renderer = window.BannerRenderer;
    const data = this.getBannerData();
    const methodName = 'render' + this.toPascalCase(bannerType || 'center-image-dynamic');
    let renderFn = renderer[methodName];

    if (typeof renderFn !== 'function') {
      renderFn = renderer.renderCenterImageDynamic;
    }

    const html = renderFn.call(renderer, data);
    container.innerHTML = html;

    if (typeof window !== 'undefined' && typeof window.AOS !== 'undefined') {
      requestAnimationFrame(function() {
        window.AOS.refreshHard();
      });
    }
    return true;
  },

  applyBannerType: function(type) {
    let bannerType = typeof type === 'string' ? type : 'center-image-dynamic';

    if (window.BannerRenderer) {
      const methodName = 'render' + this.toPascalCase(bannerType);
      if (typeof window.BannerRenderer[methodName] !== 'function') {
        bannerType = 'center-image-dynamic';
      }
    }

    const self = this;
    const maxAttempts = 20;

    function tryRender(attemptsLeft) {
      if (self.renderBannerContent(bannerType)) {
        return;
      }

      if (attemptsLeft <= 0) {
        const bannerContainer = document.getElementById('banner-container');
        if (bannerContainer) {
          bannerContainer.className = bannerContainer.className.replace(/banner-type-[^\s]+/g, '').trim();
          bannerContainer.classList.add('banner-type-' + bannerType);
        }
        return;
      }

      requestAnimationFrame(function() {
        tryRender(attemptsLeft - 1);
      });
    }

    tryRender(maxAttempts);
  },

  /**
   * DOM 로드 후 추가 적용 및 표시
   */
  applyRemainingTheme: function() {
    const config = (typeof window !== 'undefined' && window.__themeConfig) || this.getDefaultConfig();

    // 폰트 스택
    if (config.fontStack && document.body) {
      document.body.style.fontFamily = config.fontStack;

      const style = document.createElement('style');
      style.id = 'dynamic-font-style';
      const existingStyle = document.getElementById('dynamic-font-style');
      if (existingStyle) {
        existingStyle.remove();
      }
      style.textContent = 'body, body *:not([class*="pe-7s-"]):not([class*="ti-"]):not([class^="pe-7s-"]):not([class^="ti-"]), p, h1, h2, h3, h4, h5, h6 { font-family: ' + config.fontStack + ' !important; }' +
        '[class*="pe-7s-"], [class^="pe-7s-"] { font-family: "Pe-icon-7-stroke" !important; }' +
        '[class*="ti-"], [class^="ti-"] { font-family: "themify" !important; }';
      document.head.appendChild(style);
    }

    // 네비게이션 레이아웃
    if (config.navLayout && document.body) {
      document.body.classList.remove('nav-layout-top', 'nav-layout-left', 'nav-layout-right');
      document.body.classList.add('nav-layout-' + config.navLayout);
    }

    const menuScrollType = ThemeConfig ? ThemeConfig.get('menuScrollType') : config.menuScrollType;
    if (menuScrollType !== undefined && menuScrollType !== null) {
      this.applyMenuScrollType(menuScrollType);
    }

    const menuAlign = ThemeConfig ? ThemeConfig.get('menuAlign') : config.menuAlign;
    if (menuAlign !== undefined && menuAlign !== null) {
      this.applyMenuAlignment(menuAlign);
    }

    const bannerType = ThemeConfig ? ThemeConfig.get('bannerType') : config.bannerType;
    if (bannerType !== undefined && bannerType !== null) {
      this.applyBannerType(bannerType);
    }

    // 최상단 영역 배경색
    const headerTopBackgroundColor = ThemeConfig ? ThemeConfig.get('headerTopBackgroundColor') : config.headerTopBackgroundColor;
    if (headerTopBackgroundColor) {
      document.documentElement.style.setProperty('--header-top-bg-color', headerTopBackgroundColor);
    }

    // 최상단 영역 글자색
    const headerTopTextColor = ThemeConfig ? ThemeConfig.get('headerTopTextColor') : config.headerTopTextColor;
    if (headerTopTextColor) {
      document.documentElement.style.setProperty('--header-top-text-color', headerTopTextColor);
    }

    // 최상단 영역 높이
    const headerTopHeight = ThemeConfig ? ThemeConfig.get('headerTopHeight') : config.headerTopHeight;
    if (headerTopHeight !== undefined && headerTopHeight !== null) {
      const heightPx = headerTopHeight + 'px';
      document.documentElement.style.setProperty('--header-top-padding', heightPx);
      const headerTopElement = document.querySelector('.header-top');
      if (headerTopElement) {
        headerTopElement.style.paddingTop = heightPx;
        headerTopElement.style.paddingBottom = heightPx;
      }
    }

    // 최상단 영역 표시/숨김
    const headerTopVisible = ThemeConfig ? ThemeConfig.get('headerTopVisible') : config.headerTopVisible;
    if (headerTopVisible !== undefined && headerTopVisible !== null) {
      const headerTopElement = document.querySelector('.header-top');
      if (headerTopElement) {
        if (headerTopVisible === true || headerTopVisible === 'true') {
          headerTopElement.classList.remove('hidden');
          headerTopElement.style.removeProperty('display');
        } else {
          headerTopElement.classList.add('hidden');
          headerTopElement.style.removeProperty('display');
        }
      }
      this.updateNavOffsets();
    }

    // 메뉴 영역 배경색 및 글자색
    const headerBottomBackgroundColor = ThemeConfig ? ThemeConfig.get('headerBottomBackgroundColor') : config.headerBottomBackgroundColor;
    if (headerBottomBackgroundColor) {
      document.documentElement.style.setProperty('--header-bottom-bg-color', headerBottomBackgroundColor);
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.backgroundColor = headerBottomBackgroundColor;
      }
      const navLayoutEls = document.querySelectorAll('.nav-layout');
      navLayoutEls.forEach(function(el) {
        el.style.backgroundColor = headerBottomBackgroundColor;
      });
    }

    const headerBottomTextColor = ThemeConfig ? ThemeConfig.get('headerBottomTextColor') : config.headerBottomTextColor;
    if (headerBottomTextColor) {
      document.documentElement.style.setProperty('--header-bottom-text-color', headerBottomTextColor);
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.color = headerBottomTextColor;
      }
      const menuLinks = document.querySelectorAll('.header-bottom .main-menu-item > a');
      menuLinks.forEach(function(link) {
        link.style.color = headerBottomTextColor;
      });
      const submenuLinks = document.querySelectorAll('.header-bottom .submenu li a');
      submenuLinks.forEach(function(link) {
        link.style.color = headerBottomTextColor;
      });
      const navLayoutEls = document.querySelectorAll('.nav-layout');
      navLayoutEls.forEach(function(el) {
        el.style.color = headerBottomTextColor;
      });
    }

    // 메뉴 영역 높이
    const headerBottomHeight = ThemeConfig ? ThemeConfig.get('headerBottomHeight') : config.headerBottomHeight;
    if (headerBottomHeight !== undefined && headerBottomHeight !== null) {
      const heightPx = headerBottomHeight + 'px';
      document.documentElement.style.setProperty('--header-bottom-padding', heightPx);
      document.documentElement.style.setProperty('--header-bottom-padding-bottom', heightPx);
      const headerBottomEl = document.querySelector('.header-bottom');
      if (headerBottomEl) {
        headerBottomEl.style.paddingTop = heightPx;
        headerBottomEl.style.paddingBottom = heightPx;
      }
      const navLayoutEls = document.querySelectorAll('.nav-layout');
      navLayoutEls.forEach(function(el) {
        el.style.paddingTop = heightPx;
        el.style.paddingBottom = heightPx;
      });
      if (typeof window.initSidebarScrollOffset === 'function') {
        window.initSidebarScrollOffset();
      }
      this.updateNavOffsets();
    }

    // 메가메뉴 컬럼 수
    const megaMenuColumns = ThemeConfig ? ThemeConfig.get('megaMenuColumns') : config.megaMenuColumns;
    if (megaMenuColumns !== undefined && megaMenuColumns !== null) {
      const columns = parseInt(megaMenuColumns, 10);
      if (!isNaN(columns) && columns >= 1 && columns <= 3) {
        document.documentElement.style.setProperty('--mega-menu-columns', columns.toString());
      }
    }

    // Footer 테마 (DB에서 로드한 최신 값 사용 - window.__themeConfig 우선)
    const footerTheme = (typeof window !== 'undefined' && window.__themeConfig && window.__themeConfig.footerTheme)
      || (ThemeConfig ? ThemeConfig.get('footerTheme') : null)
      || config.footerTheme;
    if (footerTheme) {
      function applyFooterThemeWhenReady() {
        if (typeof window.applyFooterTheme === 'function') {
          window.applyFooterTheme(footerTheme);
        } else {
          const footerArea = document.querySelector('.footer-area');
          if (footerArea) {
            // 기존 테마 클래스 제거
            footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant');
            // 새 테마 클래스 추가
            footerArea.classList.add('footer-theme-' + footerTheme);
          } else {
            // Footer 영역이 아직 없으면 재시도 (React 컴포넌트 렌더링 대기)
            const maxRetries = 50; // 재시도 횟수 증가
            let retryCount = 0;

            function retry() {
              const footerArea = document.querySelector('.footer-area');
              if (footerArea) {
                footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant');
                footerArea.classList.add('footer-theme-' + footerTheme);
              } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(retry, 100);
              } else {
                // 최대 재시도 횟수 초과 시에도 계속 시도 (MutationObserver 사용)
                const observer = new MutationObserver(function(mutations) {
                  const footerArea = document.querySelector('.footer-area');
                  if (footerArea) {
                    footerArea.classList.remove('footer-theme-simple', 'footer-theme-modern', 'footer-theme-vibrant');
                    footerArea.classList.add('footer-theme-' + footerTheme);
                    observer.disconnect();
                  }
                });
                observer.observe(document.body, {
                  childList: true,
                  subtree: true
                });
              }
            }

            setTimeout(retry, 100);
          }
        }
      }

      // 즉시 적용 시도
      applyFooterThemeWhenReady();

      // requestAnimationFrame으로 추가 시도
      requestAnimationFrame(function() {
        requestAnimationFrame(applyFooterThemeWhenReady);
      });

      // 추가 지연 후 재시도 (React 컴포넌트 렌더링 완료 대기)
      setTimeout(applyFooterThemeWhenReady, 500);
      setTimeout(applyFooterThemeWhenReady, 1000);
    }

    // 우측 사이드바 너비
    const rightSidebarWidth = ThemeConfig ? ThemeConfig.get('rightSidebarWidth') : config.rightSidebarWidth;
    if (rightSidebarWidth !== undefined && rightSidebarWidth !== null) {
      const widthPx = rightSidebarWidth + 'px';
      document.documentElement.style.setProperty('--right-sidebar-width', widthPx);
      const sidebarElements = document.querySelectorAll('.sidebar-common-active');
      sidebarElements.forEach(function(el) {
        el.style.width = widthPx;
      });
    }

    // 우측 사이드바 테마
    const rightSidebarType = ThemeConfig ? ThemeConfig.get('rightSidebarType') : config.rightSidebarType;
    if (rightSidebarType) {
      function applyRightSidebarTypeWhenReady() {
        if (typeof window !== 'undefined' && typeof window.sidebarCommon !== 'undefined' && window.sidebarCommon && typeof window.sidebarCommon.loadSidebarType === 'function') {
          window.sidebarCommon.loadSidebarType(rightSidebarType);
        } else {
          const sidebarElement = document.querySelector('.sidebar-common-active');
          if (sidebarElement) {
            sidebarElement.classList.remove('sidebar-type-simple', 'sidebar-type-modern', 'sidebar-type-vibrant');
            sidebarElement.classList.add('sidebar-type-' + rightSidebarType);
          } else {
            const maxRetries = 30;
            let retryCount = 0;

            function retry() {
              const sidebarElement = document.querySelector('.sidebar-common-active');
              if (sidebarElement) {
                sidebarElement.classList.remove('sidebar-type-simple', 'sidebar-type-modern', 'sidebar-type-vibrant');
                sidebarElement.classList.add('sidebar-type-' + rightSidebarType);
              } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(retry, 100);
              }
            }

            setTimeout(retry, 100);
          }
        }
      }

      applyRightSidebarTypeWhenReady();
      requestAnimationFrame(function() {
        requestAnimationFrame(applyRightSidebarTypeWhenReady);
      });
    }

    // 서브메뉴 사이드바 비율
    const sidebarColumns = ThemeConfig ? ThemeConfig.get('sidebarColumns') : config.sidebarColumns;
    if (sidebarColumns !== undefined && sidebarColumns !== null) {
      function applySidebarColumnsWhenReady() {
        const sidebarElements = document.querySelectorAll('.sidebar-left, .sidebar-right');
        if (sidebarElements.length > 0) {
          const numValue = parseInt(sidebarColumns, 10);
          if (!isNaN(numValue) && numValue >= 1 && numValue <= 11) {
            const contentColumns = 12 - numValue;

            sidebarElements.forEach(function(element) {
              const classList = element.classList;
              for (let i = classList.length - 1; i >= 0; i--) {
                const className = classList[i];
                if (className.match(/^col-(xs|sm|md|lg|xl|xxl)-\d+$/)) {
                  classList.remove(className);
                }
              }
              element.classList.add('col-lg-' + numValue);
            });

            sidebarElements.forEach(function(sidebarElement) {
              let contentElement = null;
              if (sidebarElement.classList.contains('sidebar-left')) {
                contentElement = sidebarElement.nextElementSibling;
              } else if (sidebarElement.classList.contains('sidebar-right')) {
                contentElement = sidebarElement.previousElementSibling;
              }

              if (contentElement && contentElement.classList.contains('col-12')) {
                const classList = contentElement.classList;
                for (let i = classList.length - 1; i >= 0; i--) {
                  const className = classList[i];
                  if (className.match(/^col-(xs|sm|md|lg|xl|xxl)-\d+$/)) {
                    classList.remove(className);
                  }
                }
                contentElement.classList.add('col-lg-' + contentColumns);
              }
            });

            document.documentElement.style.setProperty('--sidebar-columns', numValue.toString());
          }
        } else {
          const maxRetries = 30;
          let retryCount = 0;

          function retry() {
            const sidebarElements = document.querySelectorAll('.sidebar-left, .sidebar-right');
            if (sidebarElements.length > 0) {
              applySidebarColumnsWhenReady();
            } else if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(retry, 100);
            }
          }

          setTimeout(retry, 100);
        }
      }

      applySidebarColumnsWhenReady();
      requestAnimationFrame(function() {
        requestAnimationFrame(applySidebarColumnsWhenReady);
      });
    }

    // Copyright 정렬
    const copyrightAlignment = ThemeConfig ? ThemeConfig.get('copyrightAlignment') : config.copyrightAlignment;
    if (copyrightAlignment) {
      function applyCopyrightAlignmentWhenReady() {
        if (typeof window.applyCopyrightAlignment === 'function') {
          window.applyCopyrightAlignment(copyrightAlignment);
        } else {
          const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
          const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;

          if (copyrightEl && copyrightP) {
            copyrightEl.classList.remove('text-left', 'text-center', 'text-right');
            copyrightEl.classList.remove('justify-content-start', 'justify-content-center', 'justify-content-end');
            copyrightP.classList.remove('justify-content-start', 'justify-content-center', 'justify-content-end');

            if (copyrightAlignment === 'left') {
              copyrightP.style.justifyContent = 'flex-start';
              copyrightP.style.textAlign = 'left';
            } else if (copyrightAlignment === 'center') {
              copyrightP.style.justifyContent = 'center';
              copyrightP.style.textAlign = 'center';
            } else if (copyrightAlignment === 'right') {
              copyrightP.style.justifyContent = 'flex-end';
              copyrightP.style.textAlign = 'right';
            }
          } else {
            const maxRetries = 30;
            let retryCount = 0;

            function retry() {
              const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
              const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;

              if (copyrightEl && copyrightP) {
                copyrightEl.classList.remove('text-left', 'text-center', 'text-right');
                copyrightEl.classList.remove('justify-content-start', 'justify-content-center', 'justify-content-end');
                copyrightP.classList.remove('justify-content-start', 'justify-content-center', 'justify-content-end');

                if (copyrightAlignment === 'left') {
                  copyrightP.style.justifyContent = 'flex-start';
                  copyrightP.style.textAlign = 'left';
                } else if (copyrightAlignment === 'center') {
                  copyrightP.style.justifyContent = 'center';
                  copyrightP.style.textAlign = 'center';
                } else if (copyrightAlignment === 'right') {
                  copyrightP.style.justifyContent = 'flex-end';
                  copyrightP.style.textAlign = 'right';
                }
              } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(retry, 100);
              }
            }

            setTimeout(retry, 100);
          }
        }
      }

      applyCopyrightAlignmentWhenReady();
      requestAnimationFrame(function() {
        requestAnimationFrame(applyCopyrightAlignmentWhenReady);
      });
    }

    // Copyright 배경색
    const copyrightBackgroundColor = ThemeConfig ? ThemeConfig.get('copyrightBackgroundColor') : config.copyrightBackgroundColor;
    if (copyrightBackgroundColor) {
      document.documentElement.style.setProperty('--copyright-background-color', copyrightBackgroundColor);

      let styleTag = document.getElementById('copyright-background-color-style');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'copyright-background-color-style';
        document.head.appendChild(styleTag);
      }
      styleTag.textContent = `
        .footer-area .bg-gray-3 {
          background: ${copyrightBackgroundColor} !important;
          background-color: ${copyrightBackgroundColor} !important;
        }
        .footer-area.footer-theme-simple .bg-gray-3,
        .footer-area.footer-theme-modern .bg-gray-3,
        .footer-area.footer-theme-vibrant .bg-gray-3 {
          background: ${copyrightBackgroundColor} !important;
          background-color: ${copyrightBackgroundColor} !important;
        }
      `;
    }

    // Copyright 글자색
    const copyrightTextColor = ThemeConfig ? ThemeConfig.get('copyrightTextColor') : config.copyrightTextColor;
    if (copyrightTextColor) {
      function applyCopyrightTextColorWhenReady() {
        const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
        const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;
        if (copyrightEl) {
          copyrightEl.style.color = copyrightTextColor;
        }
        if (copyrightP) {
          copyrightP.style.color = copyrightTextColor;
        }
        if (!copyrightEl) {
          const maxRetries = 30;
          let retryCount = 0;

          function retry() {
            const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
            const copyrightP = copyrightEl ? copyrightEl.querySelector('p') : null;
            if (copyrightEl) {
              copyrightEl.style.color = copyrightTextColor;
            }
            if (copyrightP) {
              copyrightP.style.color = copyrightTextColor;
            }
            if (!copyrightEl && retryCount < maxRetries) {
              retryCount++;
              setTimeout(retry, 100);
            }
          }

          setTimeout(retry, 100);
        }
      }

      applyCopyrightTextColorWhenReady();
      requestAnimationFrame(function() {
        requestAnimationFrame(applyCopyrightTextColorWhenReady);
      });
    }

    // Copyright 높이
    const copyrightHeight = ThemeConfig ? ThemeConfig.get('copyrightHeight') : config.copyrightHeight;
    if (copyrightHeight !== undefined && copyrightHeight !== null) {
      function applyCopyrightHeightWhenReady() {
        const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
        if (copyrightEl) {
          const heightPx = copyrightHeight + 'px';
          copyrightEl.style.paddingTop = heightPx;
          copyrightEl.style.paddingBottom = heightPx;
        } else {
          const maxRetries = 30;
          let retryCount = 0;

          function retry() {
            const copyrightEl = document.querySelector('.footer-area .bg-gray-3 .copyright');
            if (copyrightEl) {
              const heightPx = copyrightHeight + 'px';
              copyrightEl.style.paddingTop = heightPx;
              copyrightEl.style.paddingBottom = heightPx;
            } else if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(retry, 100);
            }
          }

          setTimeout(retry, 100);
        }
      }

      applyCopyrightHeightWhenReady();
      requestAnimationFrame(function() {
        requestAnimationFrame(applyCopyrightHeightWhenReady);
      });
    }

    // 네비게이션 오프셋 초기 업데이트
    const self = this;
    requestAnimationFrame(function() {
      self.updateNavOffsets();
    });

    // 스크롤 및 리사이즈 이벤트 리스너 등록
    this.attachNavOffsetsListeners();

    // 테마 정보에 따라 각 영역 동적 표시
    this.showThemeAreas();

    // 표시
    document.documentElement.style.visibility = 'visible';
  },

  /**
   * 초기화 함수
   */
  init: async function() {
    // 초기 렌더링 숨김
    document.documentElement.style.visibility = 'hidden';

    // DB 및 LocalStorage에서 테마 설정 비동기 로드
    await ThemeConfig.initialize();

    // 즉시 테마 적용
    this.loadAndApplyTheme();

    // DOM 로드 후 추가 적용 및 표시
    const self = this;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        self.applyRemainingTheme();
      });
    } else {
      self.applyRemainingTheme();
    }

    // 페이지 전환 시 테마 재적용 (SPA 방식 페이지 전환 대응)
    window.addEventListener('popstate', function() {
      requestAnimationFrame(function() {
        self.applyRemainingTheme();
      });
    });
  }
};

// 전역 객체로 노출 (하위 호환성)
if (typeof window !== 'undefined') {
  window.ThemeApply = ThemeApply;
  window.updateNavOffsets = function() {
    ThemeApply.updateNavOffsets();
  };
  window.applyRemainingTheme = function() {
    ThemeApply.applyRemainingTheme();
  };
}

export default ThemeApply;

